/* eslint-disable no-undef */
import { ethers } from 'ethers';

const ERC20_ABI = [
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
  'function balanceOf(address account) view returns (uint256)',
];

const PERMIT2_ADDRESS = '0x000000000022D473030F116dDEE9F6B43aC78BA3';

export async function fetchPrice(sellToken, buyToken, sellAmount, chainId = 1) {
  const params = new URLSearchParams({
    sellToken,
    buyToken,
    sellAmount,
    chainId: String(chainId),
  });

  const res = await fetch(`/api/price?${params}`);

  // Read response as text first to avoid JSON parse errors
  const rawText = await res.text();
  let data;
  try {
    data = JSON.parse(rawText);
  } catch {
    throw new Error(rawText.substring(0, 200) || `Price fetch failed (${res.status})`);
  }

  if (!res.ok) {
    throw new Error(data.reason || data.validationErrors?.[0]?.reason || data.error || `Price fetch failed (${res.status})`);
  }
  return data;
}

export async function fetchQuote(sellToken, buyToken, sellAmount, taker, chainId = 1) {
  const params = new URLSearchParams({
    sellToken,
    buyToken,
    sellAmount,
    taker,
    chainId: String(chainId),
  });

  const res = await fetch(`/api/quote?${params}`);

  // Read response as text first to avoid JSON parse errors
  const rawText = await res.text();
  let data;
  try {
    data = JSON.parse(rawText);
  } catch {
    throw new Error(rawText.substring(0, 200) || `Quote fetch failed (${res.status})`);
  }

  if (!res.ok) {
    throw new Error(data.reason || data.validationErrors?.[0]?.reason || data.error || `Quote fetch failed (${res.status})`);
  }
  return data;
}

export async function checkAndApproveToken(tokenAddress, amount, signer) {
  const nativeToken = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
  if (tokenAddress.toLowerCase() === nativeToken.toLowerCase()) {
    return null; // Native token, no approval needed
  }

  const token = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
  const owner = await signer.getAddress();
  const allowance = await token.allowance(owner, PERMIT2_ADDRESS);

  if (allowance >= BigInt(amount)) {
    return null; // Already approved
  }

  const tx = await token.approve(PERMIT2_ADDRESS, ethers.MaxUint256);
  return tx;
}

export async function executeSwap(quote, signer) {
  if (!quote.transaction) {
    throw new Error('Quote does not contain transaction data');
  }

  const { to, data, value, gas, gasPrice } = quote.transaction;

  const tx = await signer.sendTransaction({
    to,
    data,
    value: value ? BigInt(value) : 0n,
    gasLimit: gas ? BigInt(gas) : undefined,
    gasPrice: gasPrice ? BigInt(gasPrice) : undefined,
  });

  return tx;
}

export async function getTokenBalance(tokenAddress, account, provider) {
  const nativeToken = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
  if (tokenAddress.toLowerCase() === nativeToken.toLowerCase()) {
    return provider.getBalance(account);
  }
  const token = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
  return token.balanceOf(account);
}

export async function getTokenDecimals(tokenAddress, provider) {
  const nativeToken = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
  if (tokenAddress.toLowerCase() === nativeToken.toLowerCase()) return 18;
  const token = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
  return token.decimals();
}

// Block explorer URLs by chainId
const EXPLORERS = {
  1:     'https://etherscan.io',
  42161: 'https://arbiscan.io',
  8453:  'https://basescan.org',
  10:    'https://optimistic.etherscan.io',
  137:   'https://polygonscan.com',
};

export function getTxUrl(txHash, chainId = 1) {
  const base = EXPLORERS[chainId] || EXPLORERS[1];
  return `${base}/tx/${txHash}`;
}
